import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import {
  ExportMenu, FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import { formatExportDate } from '../../utils/exportData';
import {
  HiOutlineShieldCheck,
  HiOutlineRefresh,
  HiOutlineTicket,
  HiOutlineExclamationCircle,
  HiOutlineClock,
} from 'react-icons/hi';
import { ReportGuide, periodLabel } from '../../components/SuperAdmin/ReportGuide';

const formatDue = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const PERIODS = [7, 30, 90, 180, 365];

const PRIORITY_LABELS = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

const SLA_TYPE_LABELS = {
  response: 'First Response',
  resolution: 'Resolution',
};

const formatCountdown = (m) => {
  if (m == null) return '—';
  if (m < 60) return `${m}m left`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m left`;
  return `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h left`;
};

const formatOverdue = (m) => {
  if (m < 60) return `${m}m overdue`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m overdue`;
  return `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h overdue`;
};

const rateColor = (rate) =>
  rate >= 90 ? 'text-green-500' : rate >= 70 ? 'text-amber-500' : 'text-red-500';

const SlaPerformanceReport = () => {
  const [days, setDays] = useState(30);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['sla-performance', days],
    queryFn: () => superAdminAPI.getSlaPerformance({ days }),
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const response = stats.response || {};
  const resolution = stats.resolution || {};
  const byPriority = Array.isArray(body.byPriority) ? body.byPriority : [];
  const allRows = Array.isArray(body.rows) ? body.rows : [];
  const [filters, setFilters] = useState({ ...EMPTY_REPORT_FILTERS, slaType: '', state: '' });
  const rows = filterReportRows(allRows, filters, {
    searchOf: (r) => [r.ticketNumber, r.subject, r.organizationName],
  }).filter((r) =>
    (!filters.slaType || r.slaType === filters.slaType)
    && (!filters.state || (filters.state === 'overdue' ? r.overdueMinutes > 0 : !(r.overdueMinutes > 0))));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineShieldCheck className="w-7 h-7 text-primary-500" />
            On-Time Support (SLA)
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Are support tickets being answered and solved within the promised time?
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-gray-500">Show last</span>
          <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === p ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="sla-performance"
        summary="Every ticket has a promised time to reply and to solve it (the SLA). This report shows how often we keep that promise."
        points={[
          ["Replied on time", "% of tickets that got a first reply before the deadline."],
          ["Resolved on time", "% of tickets solved before the deadline."],
          ["Missed deadlines", "Times a reply or solve deadline was missed."],
          ["Open & late", "Open tickets already past their deadline: act on these first."],
          ["Due in next 24h", "Open tickets whose deadline is within a day."],
        ]}
      />

      {/* SLA compliance tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Replied on Time</p>
          <p className={`text-3xl font-bold mt-2 ${rateColor(response.complianceRate ?? 0)}`}>
            {response.complianceRate ?? 0}%
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {response.met ?? 0} on time · {response.breached ?? 0} late
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Resolved on Time</p>
          <p className={`text-3xl font-bold mt-2 ${rateColor(resolution.complianceRate ?? 0)}`}>
            {resolution.complianceRate ?? 0}%
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {resolution.met ?? 0} on time · {resolution.breached ?? 0} late
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Missed Deadlines</p>
          <p className={`text-3xl font-bold mt-2 ${
            (stats.totalBreaches ?? 0) === 0 ? 'text-green-500' : 'text-red-500'
          }`}>
            {stats.totalBreaches ?? 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">Response + resolution (last {body.windowDays ?? days} days)</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Open &amp; Late</p>
          <p className={`text-3xl font-bold mt-2 ${
            (stats.overdueOpen ?? 0) === 0 ? 'text-gray-900' : 'text-red-500'
          }`}>
            {stats.overdueOpen ?? 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">Open tickets already past their deadline</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Due in Next 24h</p>
          <p className={`text-3xl font-bold mt-2 ${
            (stats.approachingDeadline ?? 0) === 0 ? 'text-gray-900' : 'text-amber-500'
          }`}>
            {stats.approachingDeadline ?? 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">Due within the next 24 hours</p>
        </div>
      </div>

      {/* Compliance by priority */}
      {byPriority.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-700">On-Time Rate by Priority</h2>
            <ExportMenu
              fileName={`sla-compliance-by-priority-${days}d`}
              getRows={() => byPriority}
              columns={[
                { header: 'Priority', value: (p) => PRIORITY_LABELS[p.priority] || p.priority },
                { header: 'SLA Decisions', value: (p) => p.count },
                { header: 'Met', value: (p) => p.met },
                { header: 'Breached', value: (p) => p.breached },
                { header: 'Compliance %', value: (p) => p.complianceRate },
              ]}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Tickets Checked</th>
                  <th className="px-4 py-3">On Time</th>
                  <th className="px-4 py-3">Late</th>
                  <th className="px-4 py-3">On-Time %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {byPriority.map((p) => (
                  <tr key={p.priority} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[p.priority] || 'bg-gray-100 text-gray-600'}`}>
                        {PRIORITY_LABELS[p.priority] || p.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{p.count}</td>
                    <td className="px-4 py-3 text-green-600">{p.met}</td>
                    <td className="px-4 py-3 text-red-600">{p.breached}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              p.complianceRate >= 90 ? 'bg-green-500' : p.complianceRate >= 70 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, p.complianceRate)}%` }}
                          />
                        </div>
                        <span className={`font-medium ${rateColor(p.complianceRate)}`}>{p.complianceRate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tickets approaching / past SLA deadline */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Tickets Near or Past Their Deadline</h2>
          <span className="text-xs text-gray-400">
            {rows.length === allRows.length ? `${rows.length} shown` : `${rows.length} of ${allRows.length} shown`}
          </span>
        </div>

        {allRows.length > 0 && (
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            searchPlaceholder="Search ticket #, subject, organization…"
            organizations={uniqueSorted(allRows.map((r) => r.organizationName))}
            extraActive={!!(filters.slaType || filters.state)}
            onResetExtra={() => setFilters((f) => ({ ...f, slaType: '', state: '' }))}
            exportProps={{
              fileName: `sla-deadline-tickets-${days}d`,
              getRows: () => rows,
              columns: [
                { header: 'Ticket #', value: (r) => r.ticketNumber },
                { header: 'Subject', value: (r) => r.subject },
                { header: 'Organization', value: (r) => r.organizationName },
                { header: 'Priority', value: (r) => PRIORITY_LABELS[r.priority] || r.priority },
                { header: 'SLA', value: (r) => SLA_TYPE_LABELS[r.slaType] || r.slaType },
                { header: 'Deadline', value: (r) => formatExportDate(r.dueAt) },
                { header: 'Status', value: (r) => (r.overdueMinutes > 0 ? formatOverdue(r.overdueMinutes) : formatCountdown(r.minutesLeft)) },
              ],
            }}
          >
            <FilterSelect
              value={filters.slaType}
              onChange={(v) => setFilters((f) => ({ ...f, slaType: v }))}
              allLabel="All SLA Types"
              options={Object.entries(SLA_TYPE_LABELS)}
            />
            <FilterSelect
              value={filters.state}
              onChange={(v) => setFilters((f) => ({ ...f, state: v }))}
              allLabel="Overdue & approaching"
              options={[['overdue', 'Overdue only'], ['approaching', 'Approaching only']]}
            />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading SLA performance report…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allRows.length > 0
              ? 'No tickets match these filters'
              : 'No open tickets approaching or past their SLA deadline — all clear ✓'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Ticket</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Deadline For</th>
                  <th className="px-4 py-3">Deadline</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={`${row.ticketId}-${row.slaType}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <HiOutlineTicket className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900">{row.ticketNumber}</p>
                          <p className="text-xs text-gray-400 max-w-[240px] truncate" title={row.subject}>
                            {row.subject}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.organizationName || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[row.priority] || 'bg-gray-100 text-gray-600'}`}>
                        {PRIORITY_LABELS[row.priority] || row.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{SLA_TYPE_LABELS[row.slaType] || row.slaType}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDue(row.dueAt)}</td>
                    <td className="px-4 py-3">
                      {row.overdueMinutes > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          <HiOutlineExclamationCircle className="w-3.5 h-3.5" />
                          {formatOverdue(row.overdueMinutes)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                          <HiOutlineClock className="w-3.5 h-3.5" />
                          {formatCountdown(row.minutesLeft)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlaPerformanceReport;